import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { AdminLayout } from "@/components/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Lightbulb,
  Gamepad2,
  CheckCircle2,
  Clock,
  XCircle,
  Eye,
  Filter
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface BetaIdea {
  id: string;
  title: string;
  description: string;
  category: string | null;
  status: string;
  created_at: string;
  user_id: string;
  profiles?: {
    display_name: string;
  };
}

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
  reviewed: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  implemented: "bg-green-500/10 text-green-600 border-green-500/20",
  rejected: "bg-red-500/10 text-red-600 border-red-500/20",
};

const STATUS_ICONS: Record<string, React.ReactNode> = {
  pending: <Clock className="w-4 h-4" />,
  reviewed: <Eye className="w-4 h-4" />,
  implemented: <CheckCircle2 className="w-4 h-4" />,
  rejected: <XCircle className="w-4 h-4" />,
};

export default function AdminBetaIdeas() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState<BetaIdea[]>([]);
  const [filteredIdeas, setFilteredIdeas] = useState<BetaIdea[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      checkAdminAccess();
    }
  }, [user?.id]);

  useEffect(() => {
    if (isAdmin) {
      fetchIdeas();
    }
  }, [isAdmin]);

  useEffect(() => {
    filterIdeas();
  }, [ideas, statusFilter]);

  const checkAdminAccess = async () => {
    if (!user) return;

    try {
      const { data } = await supabase
        .from("profiles")
        .select("is_admin, display_name")
        .eq("id", user.id)
        .single();

      if (data?.is_admin || data?.display_name === 'miikeu') {
        setIsAdmin(true);
      } else {
        toast.error("Access denied. Admin privileges required.");
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Error checking admin access:", error);
      if (user.email?.includes('miikeu') || user.user_metadata?.display_name === 'miikeu') {
        setIsAdmin(true);
      } else {
        toast.error("Error checking admin access");
        navigate("/dashboard");
      }
    }
  };

  const fetchIdeas = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("beta_ideas")
        .select(`
          *,
          profiles!beta_ideas_user_id_fkey (
            display_name
          )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Transform the data to handle the nested profile structure
      const transformedData = (data || []).map((idea: any) => ({
        ...idea,
        profiles: idea.profiles || null,
      }));

      setIdeas(transformedData);
    } catch (error: any) {
      console.error("Error fetching ideas:", error);
      toast.error("Failed to load ideas. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const filterIdeas = () => {
    if (statusFilter === "all") {
      setFilteredIdeas(ideas);
    } else {
      setFilteredIdeas(ideas.filter(idea => idea.status === statusFilter));
    }
  };

  const updateStatus = async (ideaId: string, newStatus: string) => {
    setUpdatingStatus(ideaId);
    try {
      const { error } = await supabase
        .from("beta_ideas")
        .update({ status: newStatus })
        .eq("id", ideaId);

      if (error) throw error;

      toast.success("Status updated successfully");
      await fetchIdeas();
    } catch (error: any) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status. Please try again.");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getCategoryLabel = (category: string | null) => {
    if (!category) return "Uncategorized";
    const labels: Record<string, string> = {
      feature: "Feature",
      ui: "UI/UX",
      bug_fix: "Bug Fix",
      performance: "Performance",
      other: "Other",
    };
    return labels[category] || category;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: "Pending",
      reviewed: "Under Review",
      implemented: "Implemented",
      rejected: "Rejected",
    };
    return labels[status] || status;
  };

  const getStatusCounts = () => {
    return {
      all: ideas.length,
      pending: ideas.filter(i => i.status === 'pending').length,
      reviewed: ideas.filter(i => i.status === 'reviewed').length,
      implemented: ideas.filter(i => i.status === 'implemented').length,
      rejected: ideas.filter(i => i.status === 'rejected').length,
    };
  };

  const statusCounts = getStatusCounts();

  if (!isAdmin) {
    return null;
  }

  return (
    <AdminLayout>
      <div className="container mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Lightbulb className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold">Beta Ideas Management</h1>
          </div>
          <p className="text-muted-foreground">
            Manage and review all submitted beta ideas from users.
          </p>
        </div>

        {/* Stats and Filters */}
        <div className="mb-6 space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold">{statusCounts.all}</div>
                  <div className="text-sm text-muted-foreground">Total</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">{statusCounts.pending}</div>
                  <div className="text-sm text-muted-foreground">Pending</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{statusCounts.reviewed}</div>
                  <div className="text-sm text-muted-foreground">Reviewed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{statusCounts.implemented}</div>
                  <div className="text-sm text-muted-foreground">Implemented</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{statusCounts.rejected}</div>
                  <div className="text-sm text-muted-foreground">Rejected</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-muted-foreground" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ideas ({statusCounts.all})</SelectItem>
                <SelectItem value="pending">Pending ({statusCounts.pending})</SelectItem>
                <SelectItem value="reviewed">Under Review ({statusCounts.reviewed})</SelectItem>
                <SelectItem value="implemented">Implemented ({statusCounts.implemented})</SelectItem>
                <SelectItem value="rejected">Rejected ({statusCounts.rejected})</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Ideas List */}
        <Card>
          <CardHeader>
            <CardTitle>Ideas ({filteredIdeas.length})</CardTitle>
            <CardDescription>
              Review and manage submitted ideas. Update their status as you process them.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Gamepad2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredIdeas.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Lightbulb className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No ideas found with the selected filter.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredIdeas.map((idea) => (
                  <Card key={idea.id} className="bg-muted/50 rounded-custom-paddings">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h3 className="font-semibold text-lg">{idea.title}</h3>
                            <Badge
                              variant="outline"
                              className={STATUS_COLORS[idea.status] || ""}
                            >
                              <span className="flex items-center gap-1">
                                {STATUS_ICONS[idea.status]}
                                {getStatusLabel(idea.status)}
                              </span>
                            </Badge>
                            {idea.category && (
                              <Badge variant="secondary">
                                {getCategoryLabel(idea.category)}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <p className="text-muted-foreground mb-4 whitespace-pre-wrap">
                        {idea.description}
                      </p>
                      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>
                            by {idea.profiles?.display_name || "Anonymous"}
                          </span>
                          <span>•</span>
                          <span>
                            {new Date(idea.created_at).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Select
                            value={idea.status}
                            onValueChange={(value) => updateStatus(idea.id, value)}
                            disabled={updatingStatus === idea.id}
                          >
                            <SelectTrigger className="w-[160px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="reviewed">Under Review</SelectItem>
                              <SelectItem value="implemented">Implemented</SelectItem>
                              <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

